import { useState } from "react";
import type { FingerspellingSide } from "../../lib/fingerspelling";
import type { DrillMode } from "../../lib/fingerspelling-drill";
import type { DrillSettings } from "../../lib/fingerspelling-drill-session";
import { SideSwitch } from "./SideSwitch";

type DrillStartProps = {
  onStart: (settings: DrillSettings) => void;
  /** 苦手な文字の数。undefined のあいだ（読み込み中・取得に失敗）は、にがて だけを選べない */
  weakCount?: number | undefined;
};

const MODES: readonly {
  mode: DrillMode;
  title: string;
  description: string;
}[] = [
  {
    mode: "make",
    title: "つくる",
    description: "かなを見て、指文字をつくります",
  },
  {
    mode: "read",
    title: "よむ",
    description: "指文字を見て、かなを入力します",
  },
];

/** ドリルの開始画面。「つくる / よむ」と、手の見え方を選ぶ */
export const DrillStart = ({ onStart, weakCount }: DrillStartProps) => {
  const [mode, setMode] = useState<DrillMode>("make");
  const [side, setSide] = useState<FingerspellingSide>("aite");
  const [weakOnly, setWeakOnly] = useState(false);
  const weakSelected = mode === "read" && weakOnly;
  const noWeak = weakSelected && weakCount === 0;
  // 苦手の数が分からない（読み込み中・失敗）ときも、苦手ドリルは始められない
  const startBlocked = weakSelected && !weakCount;

  const selectMode = (next: DrillMode) => {
    setMode(next);
    if (next !== "read") setWeakOnly(false);
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        {MODES.map((item) => (
          <button
            key={item.mode}
            type="button"
            aria-pressed={mode === item.mode}
            onClick={() => selectMode(item.mode)}
            className={`select-none touch-manipulation rounded-2xl border p-4 text-left [-webkit-touch-callout:none] ${
              mode === item.mode
                ? "border-neutral-900 bg-neutral-900 text-white"
                : "border-neutral-200 bg-white"
            }`}
          >
            <div className="text-lg font-semibold">{item.title}</div>
            <div className="mt-1 text-xs opacity-80">{item.description}</div>
          </button>
        ))}
      </div>
      {mode === "read" && (
        <button
          type="button"
          aria-pressed={weakOnly}
          disabled={weakCount === undefined}
          onClick={() => setWeakOnly((prev) => !prev)}
          className={`w-full select-none touch-manipulation rounded-2xl border p-3 text-left [-webkit-touch-callout:none] disabled:opacity-40 ${
            weakOnly
              ? "border-neutral-900 bg-neutral-900 text-white"
              : "border-neutral-200 bg-white"
          }`}
        >
          にがて だけ
          {weakCount !== undefined && `（${weakCount}もじ）`}
        </button>
      )}
      {noWeak && (
        <p className="text-center text-sm text-neutral-600">
          にがては ありません
        </p>
      )}
      <SideSwitch value={side} onChange={setSide} />
      <button
        type="button"
        disabled={startBlocked}
        onClick={() =>
          onStart(
            weakSelected ? { mode, side, weakOnly: true } : { mode, side },
          )
        }
        className="w-full select-none touch-manipulation rounded-2xl bg-blue-600 py-3 text-lg font-semibold text-white disabled:opacity-40 [-webkit-touch-callout:none]"
      >
        はじめる
      </button>
    </div>
  );
};
